import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import {
  useCreateGoal,
  useCreateGroupGoal,
  useDeleteGoal,
  useGoal,
  useUpdateGoal,
  type GoalDraft,
} from "./api";
import { BackButton } from "../../components/AppShell";
import { Button, Card, Spinner } from "../../components/ui";
import { cn } from "../../lib/cn";
import { useToast } from "../../components/Toast";
import type { Cadence } from "../../lib/database.types";
import { errorMessage } from "../../lib/errors";
import { requiredGoalFor } from "./requiredGoals";

const EMPTY: GoalDraft = {
  title: "",
  description: null,
  cadence: "daily",
  target_per_period: 1,
  photo_required: false,
};

type Audience = "me" | "girls";

/**
 * Route entry point. Waits for the goal before mounting the form, so the form
 * can take its initial values as props rather than syncing them in an effect.
 */
export function GoalFormPage() {
  const { goalId } = useParams<{ goalId: string }>();
  const existing = useGoal(goalId);

  if (!goalId) return <GoalForm key="new" initial={EMPTY} />;

  if (!existing.data) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  const goal = existing.data;
  return (
    <GoalForm
      key={goal.id}
      goalId={goal.id}
      initial={{
        title: goal.title,
        description: goal.description,
        cadence: goal.cadence,
        target_per_period: goal.target_per_period,
        photo_required: goal.photo_required,
      }}
    />
  );
}

function GoalForm({
  goalId,
  initial,
}: {
  goalId?: string;
  initial: GoalDraft;
}) {
  const isEdit = Boolean(goalId);
  // Required goals are recognised by title, so the name stays fixed.
  const isRequired = isEdit && Boolean(requiredGoalFor(initial));

  const { userId } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const create = useCreateGoal(userId!);
  const createForGroup = useCreateGroupGoal();
  const update = useUpdateGoal();
  const remove = useDeleteGoal();

  const [draft, setDraft] = useState<GoalDraft>(initial);
  const [audience, setAudience] = useState<Audience>("me");

  const pending = create.isPending || createForGroup.isPending || update.isPending;
  const maxTarget = draft.cadence === "daily" ? 12 : 50;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const payload: GoalDraft = { ...draft, title: draft.title.trim() };
    if (!payload.title) return;

    try {
      if (isEdit) {
        await update.mutateAsync({ id: goalId!, patch: payload });
        toast("Goal updated", "success");
      } else if (audience === "girls") {
        const made = await createForGroup.mutateAsync(payload);
        toast(
          made === 0
            ? "Everyone already has that goal"
            : `Goal added for ${made} ${made === 1 ? "person" : "people"}`,
          "success",
        );
      } else {
        await create.mutateAsync(payload);
        toast("Goal created", "success");
      }
      navigate("/goals");
    } catch (error) {
      toast(errorMessage(error, "Could not save the goal"), "error");
    }
  }

  async function handleDelete() {
    if (!goalId) return;
    try {
      await remove.mutateAsync(goalId);
      toast("Goal deleted");
      navigate("/goals");
    } catch (error) {
      toast(errorMessage(error, "Could not delete the goal"), "error");
    }
  }

  return (
    <div className="pt-safe">
      <header className="flex items-center gap-2 pb-4">
        <BackButton onClick={() => navigate(-1)} />
        <h1 className="text-xl">{isEdit ? "edit goal" : "new goal"}</h1>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field label="What are you doing?" htmlFor="title">
          <input
            id="title"
            required
            readOnly={isRequired}
            maxLength={80}
            autoFocus={!isEdit}
            value={draft.title}
            onChange={(event) =>
              setDraft({ ...draft, title: event.target.value })
            }
            placeholder="Gym"
            className="input read-only:opacity-60"
          />
          {isRequired && (
            <p className="text-[13px] text-neutral-500">
              This is one of your daily goals, so the name can't change.
            </p>
          )}
        </Field>

        <Field label="Why / how" htmlFor="description" optional>
          <textarea
            id="description"
            rows={2}
            maxLength={500}
            value={draft.description ?? ""}
            onChange={(event) =>
              setDraft({ ...draft, description: event.target.value || null })
            }
            placeholder="Legs, push, pull. Anything counts."
            className="input min-h-[72px] resize-none"
          />
        </Field>

        <Field label="How often?">
          {/* Two options on a phone read best split down the middle, so the
              segmented control takes the full width rather than hugging. */}
          <div className="seg w-full" role="radiogroup" aria-label="How often?">
            {(["daily", "weekly"] as Cadence[]).map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={draft.cadence === option}
                onClick={() =>
                  setDraft({
                    ...draft,
                    cadence: option,
                    // 4x a week is a sane default; 1x a day is.
                    target_per_period:
                      option === "weekly"
                        ? Math.max(draft.target_per_period, 3)
                        : 1,
                  })
                }
                className="seg-opt flex-1 capitalize"
              >
                {option}
              </button>
            ))}
          </div>
        </Field>

        <Field
          label={draft.cadence === "daily" ? "Times per day" : "Times per week"}
        >
          <div className="flex items-center gap-3">
            <Stepper
              label="Decrease"
              onClick={() =>
                setDraft({
                  ...draft,
                  target_per_period: Math.max(1, draft.target_per_period - 1),
                })
              }
              disabled={draft.target_per_period <= 1}
            >
              −
            </Stepper>
            <span className="big-num w-10 text-center text-xl">
              {draft.target_per_period}
            </span>
            <Stepper
              label="Increase"
              onClick={() =>
                setDraft({
                  ...draft,
                  target_per_period: Math.min(
                    maxTarget,
                    draft.target_per_period + 1,
                  ),
                })
              }
              disabled={draft.target_per_period >= maxTarget}
            >
              +
            </Stepper>
            <p className="ml-1 text-[13px] opacity-55">
              {draft.target_per_period}× a{" "}
              {draft.cadence === "daily" ? "day" : "week"} keeps the streak
            </p>
          </div>
        </Field>

        {!isEdit && (
          <Field label="Who is this for?">
            <div className="seg w-full" role="radiogroup" aria-label="Who is this for?">
              {(
                [
                  ["me", "Just me"],
                  ["girls", "The girls"],
                ] as [Audience, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={audience === value}
                  onClick={() => setAudience(value)}
                  className="seg-opt flex-1"
                >
                  {label}
                </button>
              ))}
            </div>
            {audience === "girls" && (
              <p className="text-[13px] text-neutral-500">
                Creates this goal for everyone in the app right now.
              </p>
            )}
          </Field>
        )}

        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-neutral-300 bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={draft.photo_required}
            onChange={(event) => setDraft({ ...draft, photo_required: event.target.checked })}
            className="size-5 shrink-0 accent-[#0a0a0b]"
          />
          <span className="flex flex-col">
            <span className="text-sm font-medium">Photo required</span>
            <span className="text-[13px] text-neutral-500">
              Every check-in needs a photo.
            </span>
          </span>
        </label>

        <Button type="submit" disabled={pending} className="btn-block mt-1">
          {pending ? (
            <Spinner className="border-t-current" />
          ) : isEdit ? (
            "Save changes"
          ) : (
            "Create goal"
          )}
        </Button>
      </form>

      {isEdit && !isRequired && (
        <Card className="mt-8 gap-1.5 p-4">
          <p className="text-sm font-semibold">Delete this goal</p>
          <p className="text-[13px] opacity-60">
            Removes every check-in on it too. Archiving keeps the history.
          </p>
          <div className="mt-1 flex">
            <Button variant="danger" onClick={handleDelete}>
              delete permanently
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stepper({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "size-11 rounded-full bg-surface text-lg transition-colors",
        "hover:bg-neutral-300 disabled:opacity-40 disabled:hover:bg-surface",
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string;
  htmlFor?: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-xs opacity-60">
        {label} {optional && <span className="opacity-70">(optional)</span>}
      </label>
      {children}
    </div>
  );
}
